// Sharing (contracts/index-cards.md §5, admin-api.md "Sharing"): the growth loop step by step. Logic Index and daily
// result shares created and completed, link clicks (uniques, previews, bots left out), app opens from links and
// opens ÷ clicks; per day; shares by kind, format and channel; clicks by link kind, platform, in-app browser and
// source; the top shares (each opens its page) and sharers (linked to the player); and the current index population
// with its tiers. Every role reads. The range lives in the URL (?from&to, UTC days; default the last 28 days).

import type { CSSProperties } from "react";
import { Link, useNavigate } from "react-router";
import { type Population, type Sharing, type SharingDay, type TopShare, useSharing } from "../../api/sharing";
import { BarChart, ColumnChart, StackedColumnChart } from "../../components/charts/charts";
import { Card, Chip, EmptyState, ErrorState, KpiTile, Loading, PageHeader, Skeleton, Stat } from "../../components/ui";
import { formatDateTime, formatShortDate } from "../../lib/dates";
import { fmtInt, fmtPct } from "../../lib/format";
import {
  CHANNEL_LABEL,
  FORMAT_LABEL,
  IN_APP_LABEL,
  labelOf,
  LINK_KIND_LABEL,
  PLATFORM_LABEL,
  SHARE_KIND_LABEL,
  sharePath,
  SOURCE_LABEL,
  TIER_COLOR,
  tierColor,
  tierName,
  TIERS,
  tierOf,
} from "../../lib/sharing";
import e from "../engagement.module.css";
import pages from "../pages.module.css";
import { ClickBreakdown, OwnerLink, RangeControl, rate, readout, ShareBreakdown, TierCrest, TopTable, useRange } from "./components";
import s from "./sharing.module.css";

export function SharingPage() {
  const range = useRange();
  const { from, to, span, problem } = range;
  const q = useSharing({ from, to }, problem === null);

  const header = (
    <PageHeader
      title="Sharing"
      meta={
        <span className="mono">
          {formatShortDate(from)} – {formatShortDate(to)} UTC · {span} {span === 1 ? "day" : "days"}
        </span>
      }
      actions={<RangeControl range={range} />}
    />
  );

  if (problem) {
    return (
      <>
        {header}
        <ErrorState title="Pick another range" error={new Error(problem)} />
      </>
    );
  }
  if (q.isPending) {
    return (
      <>
        {header}
        <div className={pages.kpiRow}>
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} height={164} radius="30px" />
          ))}
        </div>
        <Loading rows={8} />
      </>
    );
  }
  if (q.isError) {
    return (
      <>
        {header}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </>
    );
  }
  return (
    <>
      {header}
      <Report data={q.data} />
    </>
  );
}

function Report({ data }: { data: Sharing }) {
  const t = data.totals;
  const shareClicks = data.clicks.byKind.filter((k) => k.key === "index_share" || k.key === "daily_share").reduce((n, k) => n + k.clicks, 0);
  return (
    <div className={s.stack}>
      <div className={pages.kpiRow} data-testid="kpis">
        <KpiTile
          palette="violet"
          label={
            <>
              Shares <small>created</small>
            </>
          }
          {...readout(t.sharesCreated)}
          foot={
            <span>
              {fmtInt(t.sharesCompleted)} completed · {fmtPct(rate(t.sharesCompleted, t.sharesCreated))}
            </span>
          }
        />
        <KpiTile
          palette="azure"
          label={
            <>
              Link <small>clicks</small>
            </>
          }
          {...readout(t.clicks)}
          foot={<span>{fmtInt(t.previews)} previews and {fmtInt(t.bots)} bots left out</span>}
        />
        <KpiTile
          palette="teal"
          label={
            <>
              Unique <small>visitors</small>
            </>
          }
          {...readout(t.uniques)}
          foot={<span>{t.uniques ? (t.clicks / t.uniques).toFixed(1) : "—"} clicks a visitor</span>}
        />
        <KpiTile
          palette="ember"
          label={
            <>
              App <small>opens</small>
            </>
          }
          {...readout(t.opens)}
          foot={<span>{fmtPct(t.opensPerClick, 1)} of clicks</span>}
        />
      </div>

      <div className={pages.statStrip} data-testid="stats">
        <Stat label="Shares completed" value={fmtInt(t.sharesCompleted)} sub={`${fmtPct(rate(t.sharesCompleted, t.sharesCreated))} of ${fmtInt(t.sharesCreated)} created`} />
        <Stat label="Unique visitors" value={fmtInt(t.uniques)} sub="distinct over the range" />
        <Stat label="Previews" value={fmtInt(t.previews)} sub="link-preview fetches, not clicks" />
        <Stat label="Opens ÷ clicks" value={fmtPct(t.opensPerClick, 1)} sub={`${fmtInt(t.opens)} app opens from links`} />
        <Stat label="Clicks per share" value={t.sharesCompleted ? (shareClicks / t.sharesCompleted).toFixed(1) : "—"} sub="share-link clicks ÷ shares completed" />
      </div>

      <div className={e.multiples} data-testid="series">
        <SharesSeries days={data.days} />
        <Series
          title="Link clicks"
          days={data.days}
          pick={(d) => d.clicks}
          unit="clicks"
          notes={(d) => [`${fmtInt(d.uniques)} unique visitors`, `${fmtInt(d.previews)} previews`]}
        />
        <Series title="Unique visitors" days={data.days} pick={(d) => d.uniques} unit="visitors" total="per day" />
        <Series title="App opens from links" days={data.days} pick={(d) => d.opens} unit="opens" notes={(d) => [`${fmtPct(rate(d.opens, d.clicks), 1)} of the day's clicks`]} />
      </div>

      <Card title="Shares" actions={<span className={pages.faint}>created in the range</span>}>
        <div className={s.breakdowns} data-testid="share-breakdowns">
          <ShareBreakdown title="By kind" rows={data.shares.byKind} labels={SHARE_KIND_LABEL} testId="shares-by-kind" />
          <ShareBreakdown title="By format" rows={data.shares.byFormat} labels={FORMAT_LABEL} testId="shares-by-format" />
          <div className={s.block} data-testid="shares-by-channel">
            <h3 className={`monoLabel ${s.blockTitle}`}>Completed, by channel</h3>
            {data.shares.byChannel.length === 0 ? (
              <span className={s.empty}>No completed shares in this range.</span>
            ) : (
              <BarChart
                label="Completed shares by channel"
                valueName="completed"
                labelWidth={112}
                rows={data.shares.byChannel.map((c) => ({ key: c.key, label: labelOf(CHANNEL_LABEL, c.key), value: c.completed }))}
              />
            )}
          </div>
        </div>
      </Card>

      <Card title="Link clicks" actions={<span className={pages.faint}>previews and bots aren't clicks</span>}>
        <div className={s.quad} data-testid="click-breakdowns">
          <ClickBreakdown title="By link" rows={data.clicks.byKind} labels={LINK_KIND_LABEL} previews testId="clicks-by-kind" />
          <ClickBreakdown title="By platform" rows={data.clicks.byPlatform} labels={PLATFORM_LABEL} previews testId="clicks-by-platform" />
          <ClickBreakdown title="By in-app browser" rows={data.clicks.byInApp} labels={IN_APP_LABEL} previews testId="clicks-by-in-app" />
          <ClickBreakdown title="By source" rows={data.clicks.bySource} labels={SOURCE_LABEL} previews testId="clicks-by-source" />
        </div>
      </Card>

      <TopShares rows={data.topShares} />

      <div className={s.pair}>
        <TopSharers rows={data.topSharers} />
        <PopulationCard population={data.population} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- per day

function SharesSeries({ days }: { days: SharingDay[] }) {
  const created = days.reduce((n, d) => n + d.sharesCreated, 0);
  return (
    <Card title="Shares created" actions={<span className="mono">{fmtInt(created)}</span>}>
      <StackedColumnChart
        label="Shares created per day"
        valueName="created"
        height={120}
        series={[
          { key: "completed", label: "Completed", color: "var(--color-state-violet)" },
          { key: "open", label: "Not completed", color: "var(--color-text-quaternary)" },
        ]}
        rows={days.map((d) => ({
          key: d.date,
          label: formatShortDate(d.date),
          values: { completed: d.sharesCompleted, open: Math.max(0, d.sharesCreated - d.sharesCompleted) },
        }))}
      />
    </Card>
  );
}

function Series({
  title,
  days,
  pick,
  unit,
  notes,
  total,
}: {
  title: string;
  days: SharingDay[];
  pick: (d: SharingDay) => number;
  unit: string;
  notes?: (d: SharingDay) => string[];
  total?: string;
}) {
  const sum = days.reduce((n, d) => n + pick(d), 0);
  return (
    <Card title={title} actions={<span className="mono">{total ?? fmtInt(sum)}</span>}>
      <ColumnChart
        label={`${title} per day`}
        valueName={unit}
        height={120}
        rows={days.map((d) => ({ key: d.date, label: formatShortDate(d.date), value: pick(d), notes: notes?.(d) }))}
      />
    </Card>
  );
}

// ---------------------------------------------------------------- top shares and sharers

function TopShares({ rows }: { rows: TopShare[] }) {
  const navigate = useNavigate();
  return (
    <Card title="Top shares" padded={false} actions={<span className={pages.faint}>the most-clicked shares in the range</span>}>
      {rows.length === 0 ? (
        <EmptyState title="No clicks yet">No share got a click in this range.</EmptyState>
      ) : (
        <TopTable
          testId="top-shares"
          headers={[
            { label: "Share" },
            { label: "Owner" },
            { label: "Result" },
            { label: "Format" },
            { label: "Channel" },
            { label: "Clicks", right: true },
            { label: "Uniques", right: true },
            { label: "Opens", right: true },
            { label: "Created" },
          ]}
        >
          {rows.map((r) => (
            <tr
              key={`${r.kind}:${r.ref}`}
              data-clickable
              data-share={r.ref}
              onClick={(ev) => !(ev.target as HTMLElement).closest("a") && navigate(sharePath(r.kind, r.ref))}
            >
              <td>
                <Link to={sharePath(r.kind, r.ref)} className={s.ref}>
                  {r.ref}
                </Link>
                <span className={s.sub}>{labelOf(SHARE_KIND_LABEL, r.kind)}</span>
              </td>
              <td>
                <OwnerLink owner={r.owner} />
              </td>
              <td>{r.kind === "index" ? <TierCrest tier={r.tier ?? (r.value !== null ? tierOf(r.value).id : null)} value={r.value} /> : <Chip tone="info">Daily #{r.puzzleNumber ?? "—"}</Chip>}</td>
              <td>{labelOf(FORMAT_LABEL, r.format)}</td>
              <td>{r.channel ? labelOf(CHANNEL_LABEL, r.channel) : <span className={pages.faint}>not completed</span>}</td>
              <td data-align="right" className="mono">
                {fmtInt(r.clicks)}
              </td>
              <td data-align="right" className="mono">
                {fmtInt(r.uniques)}
              </td>
              <td data-align="right" className="mono">
                {fmtInt(r.opens)}
              </td>
              <td className="mono">{formatDateTime(r.createdAt)}</td>
            </tr>
          ))}
        </TopTable>
      )}
    </Card>
  );
}

function TopSharers({ rows }: { rows: Sharing["topSharers"] }) {
  return (
    <Card title="Top sharers" padded={false} actions={<span className={pages.faint}>by clicks on their shares</span>}>
      {rows.length === 0 ? (
        <EmptyState title="No sharers yet">Nobody's links got a click in this range.</EmptyState>
      ) : (
        <TopTable
          testId="top-sharers"
          headers={[{ label: "Player" }, { label: "Shares", right: true }, { label: "Completed", right: true }, { label: "Clicks", right: true }, { label: "Opens", right: true }]}
        >
          {rows.map((r) => (
            <tr key={r.userId} data-user={r.userId}>
              <td>
                <OwnerLink owner={r} />
              </td>
              <td data-align="right" className="mono">
                {fmtInt(r.shares)}
              </td>
              <td data-align="right" className="mono">
                {fmtInt(r.completed)}
              </td>
              <td data-align="right" className="mono">
                {fmtInt(r.clicks)}
              </td>
              <td data-align="right" className="mono">
                {fmtInt(r.opens)}
              </td>
            </tr>
          ))}
        </TopTable>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- index population

function PopulationCard({ population: p }: { population: Population }) {
  const series = TIERS.map((t) => ({ key: t.id, label: t.name, color: TIER_COLOR[t.id] }));
  const rows = p.bins.map((b) => ({
    key: String(b.from),
    label: `${b.from}–${b.to}`,
    values: { [tierOf(b.from).id]: b.count },
    notes: [`${tierName(tierOf(b.from).id)} · ${fmtPct(b.share, 1)} of holders`],
  }));
  return (
    <Card title="Logic Index population" actions={p.computedAt ? <span className="mono">computed {formatDateTime(p.computedAt)}</span> : null}>
      <div className={s.population} data-testid="population">
        {!p.enough ? (
          <div className={s.notice} role="status" data-testid="population-notice">
            <strong>Below the minimum.</strong>
            <span>
              {fmtInt(p.holders)} index {p.holders === 1 ? "holder" : "holders"}, fewer than index.minPlayers ({fmtInt(p.minPlayers)}): players don't see their standing or the curve.
            </span>
            <Link to="/sharing/settings">Settings →</Link>
          </div>
        ) : null}
        <div className={s.facts}>
          <span>
            <strong>{fmtInt(p.holders)}</strong> holders (an index and a daily in the last 30 days)
          </span>
          <span>
            standing shown from <strong>{fmtInt(p.minPlayers)}</strong>
          </span>
          <span>the current population, whatever the range</span>
        </div>
        {p.holders === 0 || p.bins.length === 0 ? (
          <EmptyState title="No index holders yet" />
        ) : (
          <StackedColumnChart rows={rows} series={series} label="Index holders by value, coloured by tier" valueName="players" height={170} />
        )}
        {p.tiers.length ? (
          <TopTable testId="population-tiers" headers={[{ label: "Tier" }, { label: "Index" }, { label: "Players", right: true }, { label: "Share" }]}>
            {p.tiers.map((x) => (
              <tr key={x.id} data-tier={x.id}>
                <td>
                  <TierCrest tier={x.id} />
                </td>
                <td className="mono">
                  {x.min}–{x.max}
                </td>
                <td data-align="right" className="mono">
                  {fmtInt(x.count)}
                </td>
                <td>
                  <span className={s.shareCell} style={{ "--tier": tierColor(x.id) } as CSSProperties}>
                    <span className={s.shareBar}>
                      <span style={{ width: `${Math.min(100, x.share * 100)}%` }} />
                    </span>
                    <span className="mono">{fmtPct(x.share, 1)}</span>
                  </span>
                </td>
              </tr>
            ))}
          </TopTable>
        ) : null}
      </div>
    </Card>
  );
}
